import React, { useState } from 'react';
import type { Key } from '@adobe/react-spectrum';
import { HiExclamationCircle } from 'react-icons/hi';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';

import { Button } from '@/components/v2/Button';
import { ComboBox, ComboBoxItem } from '@/components/v2/Form/ComboBox/ComboBox';
import { useGetVirtualConferenceOrganizations } from '../api/getVirtualConferenceOrganizations';
import { formatDateParam, getYesterday, getToday } from '../utils';

interface FilterBarProps {
  onRun: (filters: { dateFrom: string; dateTo: string; organizationId?: string }) => void;
  isLoading: boolean;
}

const datePickerOverride: React.CSSProperties = {
  overflow: 'hidden',
};

const hideDatePickerLabel = `
  .vcd-datepicker-wrap > div > label {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
  .vcd-datepicker-wrap > div > div { margin-top: 0 !important; }
`;

export const VirtualConferenceDashboardFilterBar = ({ onRun, isLoading }: FilterBarProps) => {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(getYesterday);
  const [dateTo, setDateTo] = useState<Date | undefined>(getToday);
  const [selectedOrgId, setSelectedOrgId] = useState<Key | null>(null);

  const { data: organizations } = useGetVirtualConferenceOrganizations();

  const dateRangeInvalid = !!dateFrom && !!dateTo && dateFrom > dateTo;
  const runDisabled = !dateFrom || !dateTo || dateRangeInvalid || isLoading;

  const handleRun = () => {
    if (!dateFrom || !dateTo || dateRangeInvalid) return;
    onRun({
      dateFrom: formatDateParam(dateFrom),
      dateTo: formatDateParam(dateTo),
      organizationId: selectedOrgId ? String(selectedOrgId) : undefined,
    });
  };

  return (
    <div className="mt-6" data-cy="vcd-filter-bar">
      <style>{hideDatePickerLabel}</style>
      <div className="flex items-end gap-6">
        <div className="flex-1 min-w-0">
          <ComboBox
            label="Organization"
            testId="vcd-organization-filter"
            placeholder="Search by organization..."
            items={organizations ?? []}
            selectedKey={selectedOrgId}
            onSelectionChange={(key) => setSelectedOrgId(key)}>
            {(organizations ?? []).map((org) => (
              <ComboBoxItem key={org.id} id={org.id} data-cy={`vcd-org-item-${org.id}`}>
                {org.name}
              </ComboBoxItem>
            ))}
          </ComboBox>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-xs font-normal text-zinc-800" data-cy="vcd-date-range-label">
              Conference Date Range Filter
            </span>
            {dateRangeInvalid && (
              <span className="relative group" data-cy="vcd-date-range-error-icon">
                <HiExclamationCircle className="w-3.5 h-3.5 text-red-500" />
                <span
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block group-focus-within:block
                    whitespace-nowrap bg-red-600 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none">
                  Start date must be before end date
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="vcd-datepicker-wrap flex-1 min-w-0" style={datePickerOverride}>
              <DatePicker
                id="vcd-date-from"
                htmlName="vcd-date-from"
                selected={dateFrom}
                setSelected={setDateFrom}
                label="Start date"
              />
            </div>
            <span className="text-xs text-zinc-800 shrink-0">to</span>
            <div className="vcd-datepicker-wrap flex-1 min-w-0" style={datePickerOverride}>
              <DatePicker
                id="vcd-date-to"
                htmlName="vcd-date-to"
                selected={dateTo}
                setSelected={setDateTo}
                label="End date"
              />
            </div>
          </div>
        </div>

        <div className="shrink-0">
          <Button
            variant="primary"
            onClickHandler={handleRun}
            disabled={runDisabled}
            testId="vcd-run-button">
            Run
          </Button>
        </div>
      </div>
    </div>
  );
};
